import type { KyInstance } from 'ky';

import { ensureError, omit } from '@enterprise/core';
import type { OperationResultWithData, PagedData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { PagedMailingListQuery } from './types/paged-mailing-list-query.type';
import type { PagedMailingList } from './types/paged-mailing-list.type';

/**
 * Fetches a paginated list of mailing lists from the API.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters for filtering, sorting, and pagination.
 *   Properties `filter` and `sort` are omitted before sending the request.
 *   Defaults `isClosed` to `false` if not provided.
 * @param isExternal - Whether to use the external mailings endpoint. Defaults to `false`.
 * @returns A promise that resolves to an operation result containing paged mailing list data.
 * @throws Throws an error if the request fails.
 */
export const getPagedMailingLists = async (instance: KyInstance, query: PagedMailingListQuery, isExternal = false) => {
  try {
    const { isClosed = false, ...searchParams } = omit(query, 'filter', 'sort');

    return await instance
      .get<OperationResultWithData<PagedData<PagedMailingList>>>(
        isExternal ? Uri.mailingList.getPagedExternal : Uri.mailingList.getPaged,
        {
          searchParams: {
            ...searchParams,
            isClosed,
          },
        }
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
